<template>
  <div
    v-if="loadingStatus"
    id="page"
    class="flex flex-col items-center justify-center min-h-screen w-full"
  >
    <div class="spinner mx-auto"></div>
    <div class="p-4" style="color: rgb(78 143 243);">
      Loading, please wait...
    </div>
  </div>

  <div v-else id="page" class="min-h-screen w-full flex overflow-hiddden ">
    <design-panel
      class="min-h-screen"
      :survey="survey"
      :step="step"
      :key="step.id"
    >
    </design-panel>

    <div
      class="relative md:ml-64 w-full h-full flex flex-col min-h-screen justify-between"
      style="background-color: rgb(0 0 0 / 48%);"
    >
      <div class="fixed top-0 w-full z-20" style="left:0">
        <Navbar
          id="header"
          class="md:ml-64 bg-gray-100 border-b border-solid border-gray-200 opacity-100 py-2 z-10"
        >
          <template v-slot:left>
            <div id="name" class="flex flex-row">
              <div v-tooltip="'Back to Dashboard'">
                <router-link :to="{ name: 'campaign-list' }">
                  <svg-icon
                    icon="heroicons/outline/chevron-double-left"
                    class="h-8 w-8"
                  ></svg-icon>
                </router-link>
              </div>

              <click-to-edit
                v-model="name"
                pClass="text-xl font-bold block px-5 border border-transparent border-solid hover:border-blue-200 cursor-pointer rounded text-center"
                inputClass="text-xl block px-5 cursor-pointer rounded align-middle w-full"
                :pStyle="{ width: 'fit-content', 'min-height': '1.8rem' }"
                :inputStyle="{ 'min-height': '1.8rem' }"
              ></click-to-edit>
            </div>
          </template>

          <template v-slot:right>
            <div id="button-row" class="flex flex-row mr-5">
              <div class="my-auto mr-2 font-semibold">
                {{ published ? "Active" : "Paused" }}
              </div>
              <BaseToggle
                v-model="published"
                name="published"
                class="h-6 my-auto mr-5"
              />

              <!-- Only show export button in dev mode -->
              <button
                v-if="devmode"
                @click="exportSurvey()"
                class="mr-2 hover:text-white hover:no-underline block bg-red-800 hover:bg-red-600 text-white active:bg-red-600 font-bold uppercase text-xs px-4 py-3 rounded-md shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1"
                type="button"
                style="transition: all .15s ease"
              >
                Export Survey
              </button>

              <button
                @click="saveSurvey()"
                class="block bg-purple-600 hover:bg-purple-500 text-white active:bg-indigo-600 font-bold uppercase text-xs rounded-md shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1"
                type="button"
                style="transition: all .15s ease"
                v-tooltip="'Save'"
              >
                <span v-if="saving" class="spinner-white mx-5"></span>
                <span v-else class="mx-4 "
                  >SAVE
                  </span
                >
              </button>

              <button
                @click="previewSurvey()"
                class="mr-2 hover:text-white hover:no-underline block bg-gray-800 hover:bg-gray-600 text-white active:bg-indigo-600 font-bold uppercase text-xs px-4 rounded-md shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1"
                type="button"
                style="transition: all .15s ease"
                v-tooltip="'Preview'"
              >
                <span class="material-icons">
                  visibility
                </span>
              </button>

              <router-link
                :to="{ path: '/settings/' + survey.id }"
                class="no-underline hover:no-underline"
              >
                <button
                  class="mr-2 hover:text-white no-underline hover:no-underline block bg-green-700 hover:bg-green-500 text-white active:bg-green-600 font-bold uppercase text-xs px-4 py-3 rounded-md shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1"
                  type="button"
                  style="transition: all .15s ease"
                  v-tooltip="'Launch'"
                >
                  Launch →
                </button>
              </router-link>
            </div>
          </template>
        </Navbar>
      </div>
      <header-notification></header-notification>

      <!--Delete Modal -->
      <BaseModal
        :showModal="showDeleteModal"
        v-on:close="showDeleteModal = false"
        v-on:confirm="
          deleteStep();
          showDeleteModal = false;
        "
      >
        <template v-slot:body>
          <p class="text-sm text-gray-500">
            <strong
              >Warning: you will lose any existing responses you have received
              for this question.
            </strong>
            <br />

            Are you sure you want to delete this question and lose existing
            responses? This action cannot be undone.
          </p>
        </template>
      </BaseModal>

      <!--Image Select Modal -->
      <keep-alive>
        <image-select-modal
          v-if="showImageModal"
          @close="closeModal()"
          :accentColor="survey.accentColor"
          :option="imageModalObject"
        ></image-select-modal>
      </keep-alive>

      <!--Spacer to allow room for fixed header-->
      <div id="layout-right" style="margin-top: 57px" class="relative"></div>


      <ModalStep v-if="step.type=='MODAL'" :key="step.id" :survey="survey" :step="step" />

      <FlexStep v-else :key="step.id" :survey="survey" :step="step" />

      <builder-footer
        :survey="survey"
        :currentStep="stepIndex"
        :step="step"
        :saving="saving"
        @add-step="addStep"
        @clone-step="cloneStep"
        @select-step="setStep"
        @confirm-delete="confirmDelete"
      ></builder-footer>
    </div>
  </div>
</template>

<script>
//import { FontFamily } from "@/mixins/FontFamily.js";
//import ImageOption from "@/components/Builder/ImageOption.vue";
//import TextOption from "@/components/Builder/TextOption.vue";
//import ClickToRte from "@/components/ClickToRte.vue";
//import ImageSelectModal from "@/components/Builder/ImageSelectModal.vue";
//import WelcomeStep from "@/components/Builder/WelcomeStep.vue";
//import CloseStep from "@/components/Builder/CloseStep.vue";
//import SurveyButton from "@/components/Survey/SurveyButton.vue";

import Navbar from "@/components/Header.vue";
import DesignPanel from "@/components/Builder/DesignPanel.vue";
import ClickToEdit from "@/components/ClickToEdit.vue";
import BuilderFooter from "@/components/Builder/Footer.vue";
import HeaderNotification from "@/components/HeaderNotification.vue";
import FlexStep from "@/components/Builder/FlexStep.vue";
import ModalStep from "@/components/Builder/ModalStep.vue";

import Survey from "@/models/Survey";
import Step from "@/models/Step";
import Option from "@/models/Option";

import { ConsoleLogApiErrors } from "@/mixins/Mixins.js";
import { mapState } from "vuex";
import router from "@/router/route.js";
import EventBus from "@/event-bus.js";

export default {
  name: "survey-builder",
  mixins: [ConsoleLogApiErrors],
  components: {

    Navbar,
    FlexStep,
    ModalStep,
    DesignPanel,
    ClickToEdit,
    HeaderNotification,
    BuilderFooter,
    ImageSelectModal: () =>
      import(
        /* webpackPrefetch: true */ "@/components/Builder/ImageSelectModal.vue"
      ),
  },
  props: ["surveyid"],
  data() {
    return {
      stepIndex: 0,
      showDeleteModal: false,
      //showImageModal: false,
      loadingStatus: true,
      saving: false,
      imageModalObject: null,
    };
  },
  computed: {
    ...mapState("notification", ["notifications"]),
    devmode() {
      return process.env.NODE_ENV === "development" ? true : false;
    },
    font: {
      get() {
        return this.survey.font;
      },
      set(value) {
        if (value !== this.survey.font) {
          Survey.update({ where: this.survey.id, data: { font: value } });
        }
      },
    },
    showImageModal: {
      get() {
        return this.$store.state.modalOpen;
      },
      set(boolean) {
        this.$store.commit("SET_MODAL_STATE", boolean);
      },
    },
    user() {
      return this.$store.state.auth.user;
    },
    survey() {
      return Survey.query()
        .whereId(this.surveyid)
        .with("steps", (query) => {
          query
            .orderBy("order", "asc")
            .with("options", (query) => {
              query.orderBy("order", "asc").with("contents", (query) => {
                query.orderBy("order", "asc");
              });
            })
            .with("rows", (query) => {
              query.orderBy("order", "asc").with("columns", (query) => {
                query.orderBy("order", "asc").with("contents", (query) => {
                  query.orderBy("order", "asc");
                });
              });
            }).with("contents", (query) => {
              query.orderBy("order", "asc")
            });
        })
        .first();

      //if (surveyLoaded !== null) return surveyLoaded;
      //else return this.surveyDefault;
    },
    steps: {
      get() {
        return this.survey.steps;
      },
      set(steps) {
        var reorderedList = Step.reIndexList(steps);
        Step.update({
          data: reorderedList,
        });
      },
    },
    step() {
      return this.stepIndex > -1 ? this.survey.steps[this.stepIndex] : null;
    },
    /*
    containerWidth() {
      return this.step.containerWidth();
    },
    */
    options: {
      get() {
        return this.step.options;
      },
      set(options) {
        var reorderedList = Option.reIndexList(options);

        Option.update({
          data: reorderedList,
        });
      },
    },
    name: {
      get() {
        return this.survey.name;
      },
      set(value) {
        if (value !== this.survey.name) {
          Survey.update({
            where: this.survey.id,
            data: {
              name: value,
            },
          });
        }
      },
    },
    published: {
      get() {
        return this.survey.published;
      },
      async set(value) {
        if (value !== this.survey.published) {
          await Survey.update({
            where: this.survey.id,
            data: {
              published: value,
            },
          });

          this.saving = true;

          try {
            await Survey.api().post("/api/survey/save", this.survey);
          } catch (err) {
            //Log error
            this.$store.dispatch("notification/add", {
              message: "An error occured saving survey",
            });
          }

          this.saving = false;
        }
      },
    },
    /*
    title: {
      get() {
        return this.step.title;
      },
      set(value) {
        if (value !== this.step.title) {
          Step.update({
            where: this.step.id,
            data: {
              title: value,
            },
          });
        }
      },
    },
    subtitle: {
      get() {
        return this.step.subtitle;
      },
      set(value) {
        if (value !== this.step.subtitle) {
          Step.update({
            where: this.step.id,
            data: {
              subtitle: value,
            },
          });
        }
      },
    },
    */
    bgColor() {
      return this.survey.bgColor;
    },
    fontColor() {
      return this.survey.fontColor;
    },
  },
  methods: {
    async init() {
      this.loadingStatus = true;
      var id = this.$route.params.surveyid || null;

      this.devlog("init: " + id);

      if (id === null) {
        router.push({ name: "survey-list" });
      }

      //if survey is not already loaded in state, request it from the server
      if (this.survey === null || typeof this.steps !== "undefined") {
        await Survey.api().get("/api/survey/" + id, {
          dataKey: "data",
        });
      }
      this.loadingStatus = false;
    },
    exportSurvey() {
      router.push({ name: "exportsurvey", params: { survey: this.survey } });
    },
    setStep(index) {
      this.stepIndex = index;
    },
    async addStep(template = null) {
      //console.log("add step: " + template);

      var stepData = Step.seed(this.survey.id);

      if (template) {

        if (template === "modal"){
         stepData[0].type = 'MODAL';
         
         //stepData[0].contents = [ { "parent_type": "step", "order": 1, "type": "IMAGE", "marginTop": 2, "marginBottom": 2, "image": "icons/heroicons/solid/x", "image_link": null, "size": "md", "width": 35, "bgColor": null, "goto": "close", "goto_link": null } ];
        
        stepData[0].rows = [ { "parent_type": "step", "type": "COLUMN", "order": 1, "marginTop": 5, "marginBottom": 5, "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "end", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "row": null, "contents": [ { "parent_type": "column", "order": 1, "type": "IMAGE", "marginTop": 2, "marginBottom": 2, "image": "icons/heroicons/solid/x", "image_link": null, "size": "md", "width": 35, "bgColor": "#2387e5", "goto": "close", "goto_link": null } ] } ] }, { "parent_type": "step", "type": "COLUMN", "order": 2, "marginTop": 5, "marginBottom": 5, "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "row": null, "contents": [ { "parent_type": "column", "order": 1, "type": "IMAGE", "marginTop": 2, "marginBottom": 2, "image": "undraw/undraw_Around_the_world_re_n353", "image_link": null, "size": "md", "width": 200, "bgColor": null, "goto": "next", "goto_link": null } ] } ] }, { "parent_type": "step", "type": "COLUMN", "order": 3, "marginTop": 20, "marginBottom": 5, "maxWidth": 500, "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "row": null, "contents": [ { "parent_type": "column", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p style=\"text-align: center\"><strong>Introducing Social Media Integration</strong></p>", "clean_text": "<p style=\"text-align:center;\"><strong>Introducing Social Media Integration</strong></p>", "wtf": "what the hell", "fontColor": "#0d72d7", "fontSize": 24 }, { "parent_type": "column", "order": 2, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p style=\"text-align: center\">Put your meeting schedule on autopilot with our latest feature! Sync your sales team’s calendar to book meeting automatically. </p>", "clean_text": "<p style=\"text-align:center;\">Put your meeting schedule on autopilot with our latest feature! Sync your sales team’s calendar to book meeting automatically. </p>", "wtf": "what the hell", "fontColor": null, "fontSize": 18 } ] } ] }, { "parent_type": "step", "type": "COLUMN", "order": 4, "marginTop": 5, "marginBottom": 5, "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "row": null, "contents": [ { "parent_type": "column", "order": 1, "type": "BUTTON", "marginTop": 2, "marginBottom": 2, "text": "<p>Learn More</p>", "clean_text": "<p>Learn More</p>", "fontSize": 16, "fontColor": null, "bgColor": null, "borderColor": null, "borderRadius": 10, "goto": "next", "goto_link": null, "paddingX": 20, "paddingY": 8 } ] } ] } ];
        stepData[0].order = this.stepIndex + 1 + 0.5;

        }

        if (template === "image"){

          stepData[0].options = [ { "image": "undraw/undraw_Mobile_life_re_jtih", "image_link": null, "goto": "next", "goto_link": null, "order": 1, "value": null, "contents": [ { "parent_type": "option", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p><strong>The first option</strong></p>", "fontSize": 18 }, { "parent_type": "option", "order": 2, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p>Some text here</p>", "fontColor": "#465157", "fontSize": 16 } ], "selected": false }, {  "image": "undraw/undraw_feedback_h2ft", "image_link": null, "goto": "next", "goto_link": null, "order": 2, "value": null, "contents": [ { "parent_type": "option", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p><strong>The second option</strong></p>", "fontSize": 18 }, { "parent_type": "option", "order": 2, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p>Optional text here</p>", "fontColor": "#465157", "fontSize": 16 } ], "selected": false }, { "title": null, "description": null, "image": "undraw/undraw_Mention_re_k5xc", "image_link": null, "goto": "next", "goto_link": null, "order": 3, "value": null, "contents": [ { "parent_type": "option", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p><strong>The third option</strong></p>", "fontSize": 18 }, { "parent_type": "option", "order": 2, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p>Some text here</p>", "fontColor": "#465157", "fontSize": 16 } ], "selected": false } ];
          stepData[0].rows = [ { "parent_type": "step", "type": "COLUMN", "order": 1, "marginTop": 5, "marginBottom": 15, "maxWidth": 900, "columns": [ { "order": 1, "type": "CONTENT", "contents": [ { "parent_type": "column", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p style=\"text-align: left\"><strong>Which of these options is your favorite?</strong></p>", "clean_text": "This is clean demo text", "fontSize": 20 }, { "parent_type": "column", "order": 2, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p style=\"text-align: left\">This is demo text - you can delete this if you don’t need it.</p>", "clean_text": "This is clean demo text", "fontColor": null, "fontSize": 16 } ] } ] }, { "parent_type": "step", "type": "IMAGE", "order": 3, "columns": [] }, { "parent_type": "step", "type": "COLUMN", "order": 4, "marginTop": 20, "marginBottom": 5, "maxWidth": 900, "bgColor": null, "columns": [ { "order": 1, "type": "CONTENT", "contents": [ { "parent_type": "column", "order": 1, "type": "BUTTON", "marginTop": 2, "marginBottom": 2, "text": "<p>Next</p>", "clean_text": "<p>Next</p>", "fontSize": 15, "borderRadius": 10, "goto": "next", "goto_link": null, "paddingX": 20, "paddingY": 8 } ] } ] } ];
          stepData[0].order = this.stepIndex + 1 + 0.5;
        }

        else if (template === "text") {

          stepData[0].options = [ { "goto": "next", "goto_link": null, "order": 1, "value": null, "contents": [ { "parent_type": "option", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "Option 1", "clean_text": "Option 1", "fontSize": 18 } ], "selected": false }, { "goto": "next", "goto_link": null, "order": 2, "value": null, "contents": [ { "parent_type": "option", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "Option 2", "clean_text": "Option 2", "fontSize": 18 } ], "selected": false }, { "goto": "next", "goto_link": null, "order": 3, "value": null, "contents": [ { "parent_type": "option", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p>Option 3</p>", "clean_text": "<p>Option 3</p>", "fontSize": 18 } ], "selected": false } ];
          stepData[0].rows = [ { "parent_type": "step", "type": "COLUMN", "order": 1, "marginTop": 5, "marginBottom": 10, "maxWidth": 800, "bgColor": "#FFFFFF", "columns": [ { "order": 1, "type": "CONTENT", "bgColor": "#1DA1F2", "borderColor": "#1DA1F2", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "contents": [ { "parent_type": "column", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p style=\"text-align: left\"><strong>What is the best option?</strong></p>", "fontSize": 20 }, { "parent_type": "column", "order": 2, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p style=\"text-align: left\">Choose one of the options below</p>", "fontColor": "#5d5e5f", "fontSize": 16 } ] } ] }, { "parent_type": "step", "type": "COLUMN", "order": 2, "marginTop": 5, "marginBottom": 5, "maxWidth": 800, "columns": [ { "order": 1, "type": "OPTION", "flex": "row", "align": "center", "justify": "center", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "contents": [] } ] }, { "parent_type": "step", "type": "COLUMN", "order": 3, "marginTop": 10, "marginBottom": 5, "maxWidth": 800, "bgColor": "#FFFFFF", "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "contents": [ { "parent_type": "column", "order": 1, "type": "BUTTON", "marginTop": 2, "marginBottom": 2, "text": "<p>Skip</p>", "fontSize": 15, "borderRadius": 10, "goto": "next", "goto_link": null, "paddingX": 20, "paddingY": 8 } ] } ] } ];
          stepData[0].order = this.stepIndex + 1 + 0.5; //place it halfway after the current step, then let the auto-reorder set the value
            
        } else if (template === "welcome") {
          
          stepData[0].rows = [ { "parent_type": "step", "type": "COLUMN", "order": 8, "marginTop": 5, "marginBottom": 5, "maxWidth": 800, "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "row": null, "contents": [ { "parent_type": "column", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p style=\"text-align: left\"><strong>Welcome to AppCloud</strong></p>", "clean_text": "<p style=\"text-align:left;\"><strong>Welcome to AppCloud</strong></p>", "fontColor": null, "fontSize": 28 } ] } ] }, { "parent_type": "step", "type": "COLUMN", "order": 22, "marginTop": 0, "marginBottom": 5, "maxWidth": 800, "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "row": null, "contents": [ { "parent_type": "column", "order": 1, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p>We’re psyched to have you onboard! 🔥❤️</p>", "clean_text": "<p>We’re psyched to have you onboard! 🔥❤️</p>", "fontSize": 18 }, { "parent_type": "column", "order": 2, "type": "TEXT", "marginTop": 2, "marginBottom": 2, "text": "<p>Let’s get started with a few quick questions to help us customize your experience.</p>", "clean_text": "<p>Let’s get started with a few quick questions to help us customize your experience.</p>", "fontColor": null, "fontSize": 18 } ] } ] }, { "parent_type": "step", "type": "COLUMN", "order": 28, "marginTop": 10, "marginBottom": 5, "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "row": null, "contents": [ { "parent_type": "column", "order": 1, "type": "IMAGE", "marginTop": 2, "marginBottom": 2, "image": "undraw/undraw_reading_0re1", "image_link": null, "size": "md", "width": 200, "goto": "next", "goto_link": null } ] } ] }, { "parent_type": "step", "type": "COLUMN", "order": 31, "marginTop": 20, "marginBottom": 5, "maxWidth": 800, "columns": [ { "order": 1, "type": "CONTENT", "flex": "col", "align": "center", "justify": "evenly", "paddingL": 0, "paddingR": 0, "paddingT": 0, "paddingB": 0, "marginL": 0, "marginR": 0, "row": null, "contents": [ { "parent_type": "column", "order": 1, "type": "BUTTON", "marginTop": 2, "marginBottom": 2, "text": "<p>Let's Go!</p>", "clean_text": "<p>Let's Go!</p>", "fontSize": 16, "borderRadius": 10, "goto": "next", "goto_link": null, "paddingX": 20, "paddingY": 8 } ] } ] } ];
          stepData[0].order = 0.5;
        } else {
          stepData[0].order = this.stepIndex + 1 + 0.5;
        }
      }

      await Step.insert({
        data: stepData,
      });

      //go to first step
      if (template === "welcome") {
        this.stepIndex = 0;
      }

      //go to next step
      else {
        this.stepIndex++;
      }

      this.$store.dispatch("notification/add", {
        message: "New step successfully created.",
      });
    },
    async cloneStep() {
      let stepClone = Step.clone(this.step);

      var step = await Step.insertNew({
        data: stepClone,
      });

      this.stepIndex = this.stepIndex + 1;
      this.$store.dispatch("notification/add", {
        message: "New step created.",
      });
    },
    confirmDelete() {
      this.showDeleteModal = true;
    },
    async deleteStep() {
      var self = this;
      //Delete step from database
      await Step.api()
        .delete("/api/step/delete/" + this.step.id)
        .then(() => {
          //On success, delete step from entities state
          Step.delete(this.step.id);
          self.$store.dispatch("notification/add", {
            message: "Step has been deleted.",
          });
          self.stepIndex = 0;
        })
        .catch(function(error) {
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.log(error.response.data);
            console.log(error.response.status);
            console.log(error.response.headers);
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.log(error.request);
          } else {
            // Something happened in setting up the request that triggered an Error
            console.log("Error", error.message);
          }
          console.log(error.config);
          self.$store.dispatch("notification/add", {
            message: "An error occured deleting step",
          });
        });
    },
    async addOption() {
      var optionData = Option.seed(this.step.id);

      Option.insert({
        data: optionData,
      });

      //Option.api().post("/api/option/store", optionData);
    },
    async removeOption(option) {
      var result = await Option.api().delete("/api/option/delete/" + option.id);

      if (result.response.status == 200) {
        //Delete step from entities state
        option.$delete();
      } else {
        //Delete failed
        this.$store.dispatch("notification/add", {
          message: "An error occured deleting option",
        });
      }
    },
    cloneOption(option) {
      let optionClone = Option.clone(option);

      Option.insert({
        data: optionClone,
      });
    },
    async previewSurvey() {
      this.saving = true;

      try {
        //var post = await Survey.api().post("/api/survey/save", this.survey);
      } catch (err) {
        //Log error
        this.$store.dispatch("notification/add", {
          message: "An error occured saving survey",
        });
      }

      this.saving = false;

      router.push({ path: "/preview/" + this.survey.id + "/start" });
    },
    async saveSurvey() {
      this.saving = true;

      try {
        var post = await Survey.api().post("/api/survey/save", this.survey);
      } catch (err) {
        //Log error
        this.$store.dispatch("notification/add", {
          message: "An error occured saving survey. Please try again.",
        });
      }

      this.saving = false;

      if (post.response.status == 200) {
        this.$store.dispatch("notification/add", {
          message: post.response.data.success.message,
        });
      } else {
        this.$store.dispatch("notification/add", {
          message: post.response.data.error.message,
        });
      }
    },
    closeModal: function() {
      this.showImageModal = false;
      window.removeEventListener("keydown", this.onEscape);
    },
    openModal: function(object) {
      this.imageModalObject = object;
      this.showImageModal = true;
      window.addEventListener("keydown", this.onEscape);
    },
    onEscape: function(e) {
      if (this.showImageModal && e.keyCode === 27) {
        this.closeModal();
      }
    },
  },
  created() {
    this.init();
    EventBus.$on("open-modal", (option) => {
      this.openModal(option);
    });
  },
  destroyed() {
    window.removeEventListener("keydown", this.onEscape);
  },
};
</script>

<style>
@import "../assets/widget.css";
#page {
  overflow-x: hidden;
}

/* Add option animation */
.list-enter-active {
  transition: all 1s;
}

.list-enter /* .list-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: translateY(30px);
}

/* Step change Animation */
.slide-leave-active {
  transition: 0.01s;
}
.slide-enter-active {
  transition: 0.8s;
}
.slide-enter {
  transform: translate(100%, 0);
}
.slide-leave-to {
  transform: translate(-100%, 0);
}

.sortable-chosen {
  opacity: 1;
}

.sortable-chosen div {
  opacity: 1;
}

.sortable-ghost {
  border: 2px dashed rgb(203, 160, 204);
  border-radius: 10px;
  background: rgb(250, 233, 249);
}
.sortable-ghost div {
  opacity: 0;
}

.sortable-drag {
}
</style>
